import { useEffect, useRef, useState } from "react";
import { IconClose } from "./icons";
import { cx } from "./ui";

/**
 * 画像1枚のタグを編集する小さなポップオーバー。
 * Enter か候補クリックで追加、× で外し、「保存」で確定。
 */
export function TagEditor({
  tags,
  known,
  onSave,
  onClose,
}: {
  tags: string[];
  /** 既存タグの候補（使用回数の多い順） */
  known: string[];
  onSave: (tags: string[]) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<string[]>(tags);
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => inputRef.current?.focus(), []);

  const add = (raw: string) => {
    const t = raw.trim().replace(/\s+/g, " ").slice(0, 24);
    if (!t) return;
    setDraft((d) => (d.includes(t) || d.length >= 8 ? d : [...d, t]));
    setInput("");
  };

  const suggestions = known
    .filter((k) => !draft.includes(k) && (!input || k.toLowerCase().includes(input.toLowerCase())))
    .slice(0, 6);

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-2.5 shadow-xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mb-1.5 flex flex-wrap gap-1">
        {draft.length === 0 && <span className="text-[12px] text-slate-400">タグはまだありません</span>}
        {draft.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded-lg bg-brand-50 px-2 py-1 text-[12.5px] font-semibold text-brand-700"
          >
            {t}
            <button
              type="button"
              onClick={() => setDraft((d) => d.filter((x) => x !== t))}
              className="text-brand-400 hover:text-brand-700"
              title="外す"
            >
              <IconClose className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>

      <input
        ref={inputRef}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (input.trim()) add(input);
          }
          if (e.key === "Escape") onClose();
          e.stopPropagation();
        }}
        placeholder="タグを入力して Enter（例：A案、クライアント提出）"
        className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-[13px] outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
      />

      {suggestions.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {suggestions.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => add(k)}
              className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-0.5 text-[12px] font-medium text-slate-600 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
            >
              + {k}
            </button>
          ))}
        </div>
      )}

      <div className="mt-2 flex justify-end gap-1.5">
        <button type="button" onClick={onClose} className="chip">
          キャンセル
        </button>
        <button
          type="button"
          onClick={() => {
            // 入力途中のものも拾ってから保存
            const pending = input.trim();
            const final =
              pending && !draft.includes(pending) && draft.length < 8 ? [...draft, pending] : draft;
            onSave(final);
            onClose();
          }}
          className={cx("chip border-brand-400 bg-brand-500 font-bold text-white hover:bg-brand-600 hover:text-white")}
        >
          保存
        </button>
      </div>
    </div>
  );
}
